import { memo, type MouseEvent, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import {
    CalendarOutlined,
    CalculatorOutlined,
    DollarOutlined,
    ToolOutlined,
} from '@ant-design/icons';
import { Menu, type MenuProps, message } from 'antd';
import cn from 'classnames';

import {
    PlanSelector,
    useLazyGetMigrationStatusQuery,
    useMigrateMutation,
} from '@/features/plan-migration';

import { useSessionFeatures } from '@/entities/auth';

import * as route from '@/shared/constants/router';
import { useLanguage } from '@/shared/lib/hooks';
import { Modal } from '@/shared/ui/modal';
import { PageLoader } from '@/shared/ui/page-loader';

import ChatBotIcon from '@/shared/assets/icons/chat-bot.svg';
import DashboardIcon from '@/shared/assets/icons/dashboard-pictogram.svg';
import GraduationIcon from '@/shared/assets/icons/graduation.svg';
import JournalIcon from '@/shared/assets/icons/journal-pictogram.svg';
import PatternStudioIcon from '@/shared/assets/icons/pattern-studio.svg';

import cls from './app-menu.module.css';

type MenuItem = Required<MenuProps>['items'][number];

interface AppMenuDefaultProps {
    className?: string;
    'data-tour'?: string;
    isMobile: false;
    onClose?: never;
}

interface AppMenuMobileProps {
    className?: string;
    'data-tour'?: string;
    isMobile: true;
    onClose: VoidFunction;
}

type AppMenuProps = AppMenuDefaultProps | AppMenuMobileProps;

export const AppMenu = memo((props: AppMenuProps) => {
    const { className, isMobile, onClose } = props;
    const dataTour = props['data-tour'];
    const { t } = useTranslation();
    const { t: tSubscription } = useTranslation('subscription');
    const navigate = useNavigate();
    const [language] = useLanguage();
    const { pathname } = useLocation();
    const [openKeys, setOpenKeys] = useState<string[]>([]);
    const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);

    const sessionFeatures = useSessionFeatures();

    const PATTERN_STUDIO_FEATURE = 'pattern-studio';
    const ECONOMIC_CALENDAR_FEATURE = 'economic-calendar';

    const hasPatternStudioAccess = sessionFeatures.includes(
        PATTERN_STUDIO_FEATURE,
    );
    const hasNewsCalendarAccess = sessionFeatures.includes(
        ECONOMIC_CALENDAR_FEATURE,
    );

    const UPGRADE_REQUIRED_TITLE = t('upgradeRequired');

    // Plan upgrade modal
    const [
        getMigrationStatus,
        { data: migrationData, isLoading: isLoadingPlans },
    ] = useLazyGetMigrationStatusQuery();
    const [migrate, { isLoading: isMigrating }] = useMigrateMutation();
    const [selectedPriceId, setSelectedPriceId] = useState<string | null>(null);

    const handleOpenChange = (keys: string[]) => {
        setOpenKeys(keys);
    };

    const handleShowUpgrade = useCallback(() => {
        getMigrationStatus();
        setIsUpgradeModalOpen(true);
    }, [getMigrationStatus]);

    const handleCloseUpgrade = useCallback(() => {
        setIsUpgradeModalOpen(false);
        setSelectedPriceId(null);
    }, []);

    const handleUpgradeSelectPlan = useCallback(
        async (priceId: string) => {
            setSelectedPriceId(priceId);

            try {
                const result = await migrate({
                    stripe_price_id: priceId,
                }).unwrap();

                window.location.href = result.checkout_url;
            } catch {
                setSelectedPriceId(null);
                message.error(tSubscription('migration.error'));
            }
        },
        [migrate, tSubscription],
    );

    const selectedKeys = useMemo(() => {
        const paths = [pathname];

        if (pathname.startsWith('/pattern-studio')) {
            paths.push(route.getPatternStudioOverviewPath());
        }

        if (pathname.startsWith('/trading-journal/agent')) {
            paths.push(route.getTradingJournalAgentPath());
        }

        return paths;
    }, [pathname]);

    const handleClassicToolsClick = useCallback(
        (e: MouseEvent) => {
            e.stopPropagation();

            const classicToolsPath = route.getClassicToolsPath();

            setOpenKeys([...openKeys, classicToolsPath]);

            navigate(route.getLotSizeCalculatorPath());

            if (isMobile && onClose) {
                onClose();
            }
        },
        [isMobile, onClose, navigate, openKeys],
    );

    const menuItems = useMemo<MenuItem[]>(
        () => [
            {
                ...(isMobile ? { onClick: onClose } : {}),
                key: route.getDashboardPath(),
                label: (
                    <Link to={route.getDashboardPath()}>
                        {t('dashboardPageTitle')}
                    </Link>
                ),
                icon: <DashboardIcon />,
            },

            {
                ...(isMobile ? { onClick: onClose } : {}),
                key: route.getTradingJournalPath(),
                label: (
                    <Link to={route.getTradingJournalPath()}>
                        {t('tradingJournalPageTitle')}
                    </Link>
                ),
                icon: <JournalIcon />,
            },
            {
                ...(isMobile ? { onClick: onClose } : {}),
                key: route.getTradingJournalAgentPath(),
                label: (
                    <Link to={route.getTradingJournalAgentPath()}>
                        {t('tradingJournalAgent')}
                    </Link>
                ),
                icon: <ChatBotIcon />,
            },
            {
                ...(isMobile ? { onClick: onClose } : {}),
                key: route.getNewsCalendarPath(),
                label: hasNewsCalendarAccess ? (
                    <Link to={route.getNewsCalendarPath()}>
                        {t('newsCalendarPageTitle')}
                    </Link>
                ) : (
                    <span
                        onClick={handleShowUpgrade}
                        style={{ cursor: 'pointer' }}
                    >
                        {t('newsCalendarPageTitle')}
                    </span>
                ),
                icon: <CalendarOutlined />,
            },
            {
                ...(isMobile ? { onClick: onClose } : {}),
                key: route.getPatternStudioOverviewPath(),
                label: hasPatternStudioAccess ? (
                    <Link to={route.getPatternStudioOverviewPath()}>
                        {t('patternStudioPageTitle')}
                    </Link>
                ) : (
                    <span
                        onClick={handleShowUpgrade}
                        style={{ cursor: 'pointer' }}
                    >
                        {t('patternStudioPageTitle')}
                    </span>
                ),
                icon: <PatternStudioIcon />,
            },
            {
                key: route.getClassicToolsPath(),
                label: (
                    <span onClick={handleClassicToolsClick}>
                        {t('classicToolsPageTitle')}
                    </span>
                ),
                icon: <ToolOutlined onClick={handleClassicToolsClick} />,
                children: [
                    {
                        ...(isMobile ? { onClick: onClose } : {}),
                        key: route.getLotSizeCalculatorPath(),
                        label: (
                            <Link to={route.getLotSizeCalculatorPath()}>
                                {t('lotSizeCalculatorPageTitle')}
                            </Link>
                        ),
                        icon: <CalculatorOutlined />,
                    },
                ],
            },
            // Affiliate: always visible, the page itself offers the opt-in
            {
                ...(isMobile ? { onClick: onClose } : {}),
                key: route.getAffiliatePath(),
                label: (
                    <Link to={route.getAffiliatePath()}>
                        {t('affiliatePageTitle')}
                    </Link>
                ),
                icon: <DollarOutlined />,
            },
            {
                ...(isMobile ? { onClick: onClose } : {}),
                key: 'academy',
                label: (
                    <a
                        href={`https://www.algotechnologies.ai/${language}/academy`}
                        rel="noopener noreferrer"
                        target="_blank"
                    >
                        {t('academyLinkTitle')}
                    </a>
                ),
                icon: <GraduationIcon />,
            },
        ],
        [
            isMobile,
            onClose,
            t,
            hasPatternStudioAccess,
            hasNewsCalendarAccess,
            handleClassicToolsClick,
            language,
            handleShowUpgrade,
        ],
    );

    const upgradeModalContent = useMemo(() => {
        if (isLoadingPlans || !migrationData) {
            return <PageLoader />;
        }

        return (
            <PlanSelector
                isLoading={isMigrating}
                migrationData={migrationData}
                onSelectPlan={handleUpgradeSelectPlan}
                selectedPriceId={selectedPriceId}
            />
        );
    }, [
        handleUpgradeSelectPlan,
        isLoadingPlans,
        isMigrating,
        migrationData,
        selectedPriceId,
    ]);

    return (
        <>
            <Menu
                data-tour={dataTour}
                items={menuItems}
                mode="inline"
                onOpenChange={handleOpenChange}
                openKeys={openKeys}
                rootClassName={cn(cls.root, className)}
                selectedKeys={selectedKeys}
            />

            <Modal
                content={upgradeModalContent}
                desktopWidth="80%"
                isOpen={isUpgradeModalOpen}
                modalProps={{ destroyOnClose: true }}
                onClose={handleCloseUpgrade}
                title={UPGRADE_REQUIRED_TITLE}
            />
        </>
    );
});

AppMenu.displayName = 'AppMenu';
